// Google Analytics 4 Service for StartupRahi
import ReactGA from 'react-ga4';

// Configuration
const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || '';
const isDevelopment = process.env.NODE_ENV === 'development';
const isProduction = process.env.NODE_ENV === 'production';

class AnalyticsService {
  private initialized = false;

  /**
   * Initialize Google Analytics
   */
  initialize() {
    if (this.initialized) {
      console.log('✅ Analytics already initialized');
      return;
    }

    if (!MEASUREMENT_ID) {
      console.warn('⚠️ GA Measurement ID not found in environment variables');
      return;
    }

    // Track in development if you want (change to false to disable dev tracking)
    const shouldTrack = isProduction || true; // Set to false to disable dev tracking

    if (!shouldTrack) {
      console.log('🔍 Analytics: Development mode - tracking disabled');
      return;
    }

    try {
      ReactGA.initialize(MEASUREMENT_ID, {
        gaOptions: {
          cookieFlags: 'SameSite=None;Secure',
          anonymizeIp: true, // GDPR compliance
        },
        gtagOptions: {
          send_page_view: false, // We'll send manually
          debug_mode: isDevelopment, // Enable debug mode in development
        },
      });

      this.initialized = true;
      console.log('✅ Google Analytics initialized:', MEASUREMENT_ID);
      
      if (isDevelopment) {
        console.log('🔍 GA Debug Mode: ON');
      }
    } catch (error) {
      console.error('❌ Analytics initialization failed:', error);
    }
  }

  /**
   * Check if analytics is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Track page view
   */
  pageView(path: string, title?: string) {
    if (!this.initialized) return;

    try {
      ReactGA.send({
        hitType: 'pageview',
        page: path,
        title: title || document.title,
      });
      
      if (isDevelopment) {
        console.log('📊 Page view tracked:', { path, title });
      }
    } catch (error) {
      console.error('❌ Page view tracking failed:', error);
    }
  }

  /**
   * Track custom event
   */
  event(category: string, action: string, label?: string, value?: number) {
    if (!this.initialized) return;

    try {
      ReactGA.event({
        category,
        action,
        label,
        value,
      });
      
      if (isDevelopment) {
        console.log(`📊 Event tracked:`, { category, action, label, value });
      }
    } catch (error) {
      console.error('❌ Event tracking failed:', error);
    }
  }

  /**
   * Track article view
   */
  trackArticleView(articleSlug: string, articleTitle: string, category: string) {
    this.event('Article', 'View', `${category}/${articleSlug}`);
    this.event('Category', 'View', category);
    
    // Custom dimension for article title
    if (this.initialized) {
      ReactGA.gtag('event', 'article_view', {
        article_title: articleTitle,
        article_category: category,
        article_slug: articleSlug,
      });
    }
  }

  /**
   * Track article like
   */
  trackArticleLike(articleSlug: string, articleTitle: string) {
    this.event('Engagement', 'Like', articleSlug);
    
    if (this.initialized) {
      ReactGA.gtag('event', 'article_like', {
        article_title: articleTitle,
        article_slug: articleSlug,
      });
    }
  }

  /**
   * Track article unlike
   */
  trackArticleUnlike(articleSlug: string, articleTitle: string) {
    this.event('Engagement', 'Unlike', articleSlug);
    
    if (this.initialized) {
      ReactGA.gtag('event', 'article_unlike', {
        article_title: articleTitle,
        article_slug: articleSlug,
      });
    }
  }

  /**
   * Track article share
   */
  trackArticleShare(articleSlug: string, articleTitle: string, method: string) {
    this.event('Engagement', 'Share', `${method}/${articleSlug}`);
    
    if (this.initialized) {
      ReactGA.gtag('event', 'share', {
        method: method,
        content_type: 'article',
        item_id: articleSlug,
        content: articleTitle,
      });
    }
  }

  /**
   * Track search
   */
  trackSearch(searchTerm: string, resultsCount?: number) {
    this.event('Search', 'Query', searchTerm, resultsCount);
    
    if (this.initialized) {
      ReactGA.gtag('event', 'search', {
        search_term: searchTerm,
        ...(resultsCount !== undefined && { results_count: resultsCount }),
      });
    }
  }

  /**
   * Track newsletter subscription
   */
  trackSubscription(email: string) {
    this.event('Newsletter', 'Subscribe', 'Email');
    
    if (this.initialized) {
      ReactGA.gtag('event', 'sign_up', {
        method: 'newsletter',
      });
    }
  }

  /**
   * Track comment submission
   */
  trackComment(articleSlug: string, articleTitle: string) {
    this.event('Engagement', 'Comment', articleSlug);
    
    if (this.initialized) {
      ReactGA.gtag('event', 'generate_lead', {
        content_type: 'comment',
        item_id: articleSlug,
        content: articleTitle,
      });
    }
  }

  /**
   * Track category view
   */
  trackCategoryView(categoryName: string, articlesCount?: number) {
    this.event('Category', 'View', categoryName, articlesCount);
    
    if (this.initialized) {
      ReactGA.gtag('event', 'view_item_list', {
        item_list_name: categoryName,
        ...(articlesCount !== undefined && { items_count: articlesCount }),
      });
    }
  }

  /**
   * Track error
   */
  trackError(errorMessage: string, errorPage: string, errorStack?: string) {
    this.event('Error', errorMessage, errorPage);
    
    if (this.initialized) {
      ReactGA.gtag('event', 'exception', {
        description: errorMessage,
        fatal: false,
        page: errorPage,
        ...(errorStack && { stack: errorStack }),
      });
    }
  }

  /**
   * Track timing (e.g., page load time, API response time)
   */
  trackTiming(category: string, variable: string, value: number, label?: string) {
    if (!this.initialized) return;

    try {
      ReactGA.gtag('event', 'timing_complete', {
        name: variable,
        value: value,
        event_category: category,
        ...(label && { event_label: label }),
      });
      
      if (isDevelopment) {
        console.log(`⏱️ Timing tracked:`, { category, variable, value, label });
      }
    } catch (error) {
      console.error('❌ Timing tracking failed:', error);
    }
  }

  /**
   * Track outbound link click
   */
  trackOutboundLink(url: string, label?: string) {
    this.event('Outbound', 'Click', label || url);
    
    if (this.initialized) {
      ReactGA.gtag('event', 'click', {
        event_category: 'outbound',
        event_label: url,
        transport_type: 'beacon',
      });
    }
  }

  /**
   * Track file download
   */
  trackDownload(fileName: string, fileType?: string) {
    this.event('Download', 'File', fileName);
    
    if (this.initialized) {
      ReactGA.gtag('event', 'file_download', {
        file_name: fileName,
        ...(fileType && { file_extension: fileType }),
      });
    }
  }

  /**
   * Track social interaction
   */
  trackSocialClick(network: string, action: string, target?: string) {
    this.event('Social', action, network);
    
    if (this.initialized) {
      ReactGA.gtag('event', 'social_click', {
        social_network: network,
        social_action: action,
        ...(target && { social_target: target }),
      });
    }
  }

  /**
   * Set user properties (for logged-in users)
   */
  setUserProperties(properties: Record<string, any>) {
    if (!this.initialized) return;

    try {
      ReactGA.gtag('set', 'user_properties', properties);
      
      if (isDevelopment) {
        console.log('👤 User properties set:', properties);
      }
    } catch (error) {
      console.error('❌ Set user properties failed:', error);
    }
  }

  /**
   * Set user ID (for logged-in users)
   */
  setUserId(userId: string) {
    if (!this.initialized) return;

    try {
      ReactGA.gtag('config', MEASUREMENT_ID, {
        user_id: userId,
      });
      
      if (isDevelopment) {
        console.log('👤 User ID set:', userId);
      }
    } catch (error) {
      console.error('❌ Set user ID failed:', error);
    }
  }
}

// Export singleton instance
const analyticsService = new AnalyticsService();
export default analyticsService;